// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { ChevronDown } from 'lucide-react';
import { EducBox, ChartBox, ResultCard, InputRow, EDUC, baseChartOpts, fmt } from './shared';

// Static educational card — no inputs, fixed values at 2.5% inflation on €10.000
const InflazioneCard = () => (
  <div style={{ background: 'rgba(201,168,76,0.06)', borderLeft: '3px solid #C9A84C', borderRadius: '0 8px 8px 0', padding: '16px 20px', marginBottom: 24 }}>
    <p style={{ color: '#C9A84C', fontSize: 15, fontWeight: 500, marginBottom: 12 }}>Quanto vale €10.000 nel tempo?</p>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {['Oggi', 'Tra 10 anni', 'Tra 20 anni', 'Tra 30 anni'].map(h => (
              <th key={h} style={{ padding: '6px 12px', textAlign: 'center', color: '#888780', fontWeight: 400, background: '#252523' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {['€10.000', '€7.812', '€6.103', '€4.767'].map((v, i) => (
              <td key={i} style={{ padding: '8px 12px', textAlign: 'center', color: '#E8E8E4', fontWeight: i === 0 ? 600 : 400, background: '#252523' }}>{v}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
    <p style={{ fontSize: 12, color: '#888780', marginTop: 10, lineHeight: 1.6 }}>
      Tenere i soldi fermi sul conto corrente non è sicuro — è certamente perdere valore ogni anno. Investire non è speculazione: è l'unico modo per proteggere il potere d'acquisto dei tuoi risparmi nel tempo.
    </p>
  </div>
);

const InflazioneCalc = () => {
  const [capitale, setCapitale] = useState('100000');
  const [inflazione, setInflazione] = useState('2.5');
  const [anni, setAnni] = useState('20');

  const C = parseFloat(capitale) || 0;
  const inf = parseFloat(inflazione) / 100;
  const n = parseInt(anni) || 0;
  const reale = C * Math.pow(1 - inf, n);
  const perdita = C - reale;
  const labels = Array.from({ length: n + 1 }, (_, i) => i % 2 === 0 ? `Anno ${i}` : '');
  const data = Array.from({ length: n + 1 }, (_, i) => Math.round(C * Math.pow(1 - inf, i)));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <InflazioneCard />
      <EducBox text={EDUC.inflazione} />
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <InputRow label="Capitale attuale (€)" value={capitale} onChange={setCapitale} prefix="€" />
        <InputRow label="Inflazione annua (%)" value={inflazione} onChange={setInflazione} suffix="%" step={0.1} min={0} max={30} tooltip="Media FOI ISTAT ultimi 20 anni (2004-2024): 2,5% — Inflazione attuale marzo 2026: 1,7% — modificabile" />
        <InputRow label="Orizzonte (anni)" value={anni} onChange={setAnni} suffix="anni" />
      </div>
      <p className="mt-1 mb-4 text-xs text-[#888780]" data-testid="erosione-foi-note">
        Media FOI ISTAT ultimi 20 anni (2004-2024): 2,5% — Inflazione attuale marzo 2026: 1,7% — modificabile
      </p>
      <div className="grid grid-cols-3 gap-4">
        <ResultCard label="Valore attuale" value={`€${fmt(C)}`} />
        <ResultCard label={`Potere d'acquisto tra ${n} anni`} value={`€${fmt(reale)}`} gold />
        <ResultCard label="Perdita di valore" value={`€${fmt(perdita)}`} />
      </div>
      <ChartBox title="Erosione del potere d'acquisto" height={420}>
        <Line data={{ labels, datasets: [{ data, borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.1)', fill: true, tension: 0.3, pointRadius: 0 }] }} options={baseChartOpts()} />
      </ChartBox>
</motion.div>
  );
};

export default InflazioneCalc;
